import {useEffect, useMemo, useState} from 'react';
import {Form, Input, Select, Spin, Switch, Tabs, Tag} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {Button} from '@dabafinance/react-components';
import {
  GET_DISCLOSURE_LANGUAGES,
  GET_DISCLOSURES_APP_CONFIGURATION,
  UPDATE_APP_CONFIGURATION,
} from 'graphql/queries/mobileSettings';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import {RichTextEditor} from 'components/richTextEditor';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum, supportedLanguages} from 'utils/constants';
import plusIcon from 'images/plus-circle.png';
import deleteIcon from 'images/delete.svg';
import styles from './disclosures.module.scss';

const SURFACES = [
  {value: 'PAYMENT', label: 'Payment'},
  {value: 'SWAP', label: 'Swap'},
];

// Copy is authored in English and machine-translated onward, matching every
// other multilingual editor in Platform Settings.
const SOURCE_LANGUAGE = 'EN';

// What ReactQuill leaves behind once the admin clears the editor. Treated as
// empty so a wiped body is stored as null rather than as a stray paragraph.
const EMPTY_QUILL_HTML = '<p><br></p>';

const cleanHtml = html => {
  const value = (html || '').trim();
  return !value || value === EMPTY_QUILL_HTML ? '' : value;
};

const languageLabel = language =>
  supportedLanguages.find(({value}) => value === language)?.label || language;

/**
 * Turns the saved list into form state.
 *
 * Translations become a map keyed by language so each language gets its own
 * tab. The map is seeded from the languages the API exposes and then layered
 * with whatever was saved, which means a language the enum no longer lists
 * still survives a round trip — updateAppConfig replaces the whole disclosures
 * array, so anything missing from the map is a silent deletion.
 */
const toFormValues = (entries, languages) =>
  (entries || []).map(entry => {
    const translations = {};
    languages.forEach(language => {
      translations[language] = {title: '', body: ''};
    });
    (entry.translations || []).forEach(translation => {
      translations[translation.language] = {
        title: translation.title || '',
        body: translation.body || '',
      };
    });
    return {
      surface: entry.surface,
      version: entry.version ?? 1,
      // The version as the users currently see it. Kept beside the editable
      // version so the UI can tell "unchanged" from "about to be republished",
      // and so a bump can be taken back before saving.
      publishedVersion: entry.version ?? 1,
      active: Boolean(entry.active),
      mandatory: Boolean(entry.mandatory),
      provider: entry.provider || '',
      translations,
    };
  });

const buildDisclosuresInput = entries =>
  (entries || []).map(entry => ({
    surface: entry.surface,
    version: Number(entry.version) || 1,
    active: Boolean(entry.active),
    mandatory: Boolean(entry.mandatory),
    provider: entry.provider?.trim() ? entry.provider.trim() : null,
    translations: Object.entries(entry.translations || {})
      .map(([language, copy]) => ({
        language,
        title: copy?.title?.trim() || null,
        body: cleanHtml(copy?.body) || null,
      }))
      // A language the admin never filled in carries no copy worth storing;
      // one that has either half is kept whole.
      .filter(({title, body}) => title || body),
  }));

const hasCopy = entry =>
  Object.values(entry?.translations || {}).some(
    copy => copy?.title?.trim() || cleanHtml(copy?.body),
  );

export const Disclosures = () => {
  const [form] = Form.useForm();
  const [openFinishModal, setOpenFinishModal] = useState(false);
  const [translatingKey, setTranslatingKey] = useState(null);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.PlatformSettings),
  );

  const {data, loading, refetch} = useQuery(GET_DISCLOSURES_APP_CONFIGURATION, {
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error.message),
  });

  // Introspection can be turned off at the gateway, so a failure here is not
  // worth surfacing — the editor just falls back to the languages we ship.
  const {data: languageData} = useQuery(GET_DISCLOSURE_LANGUAGES, {
    onError: () => {},
  });

  const languages = useMemo(() => {
    const fromApi = (languageData?.__type?.enumValues || [])
      .map(({name}) => name)
      .filter(Boolean);
    const resolved = fromApi.length
      ? fromApi
      : supportedLanguages.map(({value}) => value);
    // English first so the tab an admin authors in is the one that opens.
    return [
      ...resolved.filter(language => language === SOURCE_LANGUAGE),
      ...resolved.filter(language => language !== SOURCE_LANGUAGE),
    ];
  }, [languageData]);

  const [updateAppConfig, {loading: loadingUpdate}] = useMutation(
    UPDATE_APP_CONFIGURATION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [translateText, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_TEXT,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    if (loading) return;
    form.setFieldsValue({
      disclosures: toFormValues(
        data?.getAppConfig?.config?.disclosures,
        languages,
      ),
    });
  }, [loading, data, languages, form]);

  // `preserve` matters: a language tab that has never been opened has no
  // mounted field, and the default useWatch only reports mounted ones. Without
  // it the "no copy yet" hint would accuse every unvisited language of being
  // empty. The saved payload is read from the form store directly, so it sees
  // those values either way.
  const entries = Form.useWatch('disclosures', {form, preserve: true}) || [];

  const handleTranslate = async (index, targetLanguage) => {
    const source = form.getFieldValue([
      'disclosures',
      index,
      'translations',
      SOURCE_LANGUAGE,
    ]);
    const sourceTitle = source?.title?.trim() || '';
    const sourceBody = cleanHtml(source?.body);

    if (!sourceTitle && !sourceBody) {
      toast.error('Add the English title or body first');
      return;
    }

    const translate = async sourceText => {
      if (!sourceText) return '';
      const {data: translation} = await translateText({
        variables: {
          sourceLang: SOURCE_LANGUAGE,
          targetLang: targetLanguage,
          sourceText,
        },
      });
      const response = translation?.translateText;
      if (
        response?.__typename === 'Error' ||
        response?.statusCode === 400 ||
        !response?.targetText
      ) {
        throw new Error(response?.message || 'Error translating text');
      }
      return response.targetText;
    };

    const key = `${index}-${targetLanguage}`;
    setTranslatingKey(key);
    try {
      // The body is HTML; DeepL keeps the markup, which is why the editor can
      // take the result straight back without a round trip through plain text.
      const title = await translate(sourceTitle);
      const body = await translate(sourceBody);
      const current =
        form.getFieldValue([
          'disclosures',
          index,
          'translations',
          targetLanguage,
        ]) || {};
      form.setFieldValue(
        ['disclosures', index, 'translations', targetLanguage],
        {
          title: title || current.title || '',
          body: body || current.body || '',
        },
      );
      toast.success('Translated successfully');
    } catch (error) {
      toast.error(error.message || 'Error translating text');
    } finally {
      setTranslatingKey(null);
    }
  };

  const handlePublishNewVersion = index => {
    const published = form.getFieldValue([
      'disclosures',
      index,
      'publishedVersion',
    ]);
    form.setFieldValue(
      ['disclosures', index, 'version'],
      Number(published || 0) + 1,
    );
  };

  const handleRevertVersion = index => {
    const published = form.getFieldValue([
      'disclosures',
      index,
      'publishedVersion',
    ]);
    form.setFieldValue(['disclosures', index, 'version'], published || 1);
  };

  const validate = values => {
    const list = values?.disclosures || [];
    const seen = [];
    for (const entry of list) {
      if (!entry?.surface) {
        return 'Every disclosure needs a surface';
      }
      if (seen.indexOf(entry.surface) !== -1) {
        return `There is more than one ${entry.surface} disclosure. The app would have no way to choose between them.`;
      }
      seen.push(entry.surface);
      if (entry.active && !hasCopy(entry)) {
        return `The ${entry.surface} disclosure is active but has no copy in any language`;
      }
    }
    return null;
  };

  const handleValidateThenConfirm = values => {
    const problem = validate(values);
    if (problem) {
      toast.error(problem);
      return;
    }
    setOpenFinishModal(true);
  };

  const handleSubmit = () => {
    // The whole list goes back, every time: updateAppConfig replaces the
    // `disclosures` key rather than merging into it, so sending only the
    // surface being edited would delete the others.
    const disclosures = buildDisclosuresInput(
      form.getFieldValue('disclosures'),
    );

    updateAppConfig({variables: {data: {disclosures}}})
      .then(({data: {updateAppConfig}}) => {
        if (updateAppConfig.__typename === 'Error') {
          toast.error(updateAppConfig.message);
          return;
        }
        toast.success('Updated Successfully');
        setOpenFinishModal(false);
        refetch();
      })
      .catch(error => toast.error(error.message || 'Update failed'));
  };

  const bumpedSurfaces = entries
    .filter(entry => Number(entry?.version) > Number(entry?.publishedVersion))
    .map(entry => entry?.surface)
    .filter(Boolean);

  if (loading) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading....</h1>
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <div className={styles.header}>
        <h2>Disclosures</h2>
        <p>
          Copy shown before a user hands off to an external provider. Each
          surface has one disclosure: PAYMENT covers deposits and withdrawals,
          SWAP covers currency conversion. Only active disclosures are shown,
          and a mandatory one must be accepted before the user can continue.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleValidateThenConfirm}
        disabled={!tabOperations?.update}>
        <Form.List name="disclosures">
          {(fields, {add, remove}) => (
            <>
              {fields.map(({key, name}) => {
                const entry = entries[name] || {};
                const pendingBump =
                  Number(entry.version) > Number(entry.publishedVersion);
                const missingCopy = languages.filter(language => {
                  const copy = entry.translations?.[language];
                  return !copy?.title?.trim() && !cleanHtml(copy?.body);
                });

                return (
                  <div key={key} className={styles.disclosureCard}>
                    <div className={styles.cardHeader}>
                      <div className={styles.cardTitle}>
                        <h3>
                          {SURFACES.find(s => s.value === entry.surface)
                            ?.label || 'New disclosure'}
                        </h3>
                        <Tag color={pendingBump ? 'orange' : 'default'}>
                          {pendingBump
                            ? `v${entry.publishedVersion} → v${entry.version} on save`
                            : `v${entry.version || 1}`}
                        </Tag>
                        {entry.active ? (
                          <Tag color="green">Active</Tag>
                        ) : (
                          <Tag>Inactive</Tag>
                        )}
                        {entry.mandatory && <Tag color="red">Mandatory</Tag>}
                      </div>
                      {tabOperations?.update && (
                        <img
                          src={deleteIcon}
                          alt="Remove"
                          className={styles.deleteIcon}
                          onClick={() => remove(name)}
                        />
                      )}
                    </div>

                    <div className={styles.settingsRow}>
                      <Form.Item
                        label="Surface"
                        name={[name, 'surface']}
                        rules={[{required: true, message: 'Pick a surface'}]}>
                        <Select options={SURFACES} placeholder="Surface" />
                      </Form.Item>
                      <Form.Item
                        label="Provider"
                        name={[name, 'provider']}
                        tooltip="Optional. The external provider this disclosure names, e.g. Flutterwave.">
                        <Input placeholder="e.g. Flutterwave" />
                      </Form.Item>
                      <Form.Item
                        label="Active"
                        name={[name, 'active']}
                        valuePropName="checked">
                        <Switch />
                      </Form.Item>
                      <Form.Item
                        label="Mandatory"
                        name={[name, 'mandatory']}
                        valuePropName="checked"
                        tooltip="The user must accept before continuing.">
                        <Switch />
                      </Form.Item>
                    </div>

                    {/* The version is never touched by an ordinary save. It is
                        bumped here, deliberately, because every bump re-prompts
                        users who already accepted. */}
                    <Form.Item name={[name, 'version']} hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item name={[name, 'publishedVersion']} hidden>
                      <Input />
                    </Form.Item>
                    <div className={styles.versionRow}>
                      <div className={styles.versionCopy}>
                        <span className={styles.versionLabel}>
                          Live version
                        </span>
                        <strong>v{entry.publishedVersion || 1}</strong>
                        <p>
                          Fixing a typo? Save without bumping. Publishing a new
                          version re-prompts every user who already accepted v
                          {entry.publishedVersion || 1}, so only bump when the
                          meaning of the text has changed.
                        </p>
                      </div>
                      <div className={styles.versionActions}>
                        {pendingBump ? (
                          <Button
                            type="secondary"
                            label={`Keep v${entry.publishedVersion}`}
                            onClick={() => handleRevertVersion(name)}
                          />
                        ) : (
                          <Button
                            type="secondary"
                            label="Publish new version"
                            onClick={() => handlePublishNewVersion(name)}
                          />
                        )}
                      </div>
                    </div>

                    {/* Panes are kept mounted so an edit made in French is
                        still in the form store after switching back to
                        English — every language is written on every save. */}
                    <Tabs
                      className={styles.languageTabs}
                      destroyInactiveTabPane={false}
                      items={languages.map(language => ({
                        key: language,
                        label: languageLabel(language),
                        children: (
                          <div className={styles.languagePanel}>
                            {language !== SOURCE_LANGUAGE && (
                              <div className={styles.translateRow}>
                                <Button
                                  type="secondary"
                                  label={
                                    translatingKey === `${name}-${language}`
                                      ? 'Translating…'
                                      : `Translate from ${languageLabel(
                                          SOURCE_LANGUAGE,
                                        )}`
                                  }
                                  disabled={
                                    loadingTranslation || !tabOperations?.update
                                  }
                                  onClick={() =>
                                    handleTranslate(name, language)
                                  }
                                />
                              </div>
                            )}
                            <Form.Item
                              label="Title"
                              name={[name, 'translations', language, 'title']}>
                              <Input placeholder="Disclosure title" />
                            </Form.Item>
                            {/* ReactQuill sits outside antd's disabled
                                context, so a read-only admin gets the rendered
                                body instead of a live editor. */}
                            {tabOperations?.update ? (
                              <Form.Item
                                label="Body"
                                name={[name, 'translations', language, 'body']}>
                                <RichTextEditor
                                  className={styles.editor}
                                  placeholder="What the user is agreeing to"
                                />
                              </Form.Item>
                            ) : (
                              <Form.Item label="Body">
                                <div
                                  className={styles.bodyPreview}
                                  dangerouslySetInnerHTML={{
                                    __html: cleanHtml(
                                      entry.translations?.[language]?.body,
                                    ),
                                  }}
                                />
                              </Form.Item>
                            )}
                          </div>
                        ),
                      }))}
                    />

                    {missingCopy.length > 0 && (
                      <span className={styles.warning}>
                        No copy yet for{' '}
                        {missingCopy.map(languageLabel).join(', ')}. Users on
                        those languages will see nothing.
                      </span>
                    )}
                  </div>
                );
              })}

              {tabOperations?.update && (
                <div
                  className={styles['add-button']}
                  onClick={() =>
                    add({
                      surface: undefined,
                      version: 1,
                      publishedVersion: 1,
                      active: false,
                      mandatory: false,
                      provider: '',
                      translations: Object.fromEntries(
                        languages.map(language => [
                          language,
                          {title: '', body: ''},
                        ]),
                      ),
                    })
                  }>
                  <img src={plusIcon} alt="Add" />
                  <h1>Add Disclosure</h1>
                </div>
              )}

              {!fields.length && !tabOperations?.update && (
                <span className={styles.empty}>
                  No disclosures configured yet.
                </span>
              )}
            </>
          )}
        </Form.List>

        <div className={styles.submitContainer}>
          <SubmitButton label="Update" disabled={!tabOperations?.update} />
        </div>
      </Form>

      <ConfirmModal
        openModal={openFinishModal}
        setOpenModal={() => setOpenFinishModal(false)}
        title="Confirm Update"
        loading={loadingUpdate}
        handleOperation={handleSubmit}
        confirmText={
          bumpedSurfaces.length
            ? `This publishes a new version of the ${bumpedSurfaces.join(
                ' and ',
              )} disclosure. Every user who already accepted the current version will be prompted again.`
            : 'Are you sure you want to update the disclosures?'
        }
      />
    </div>
  );
};
